import _styled2styled_components from 'styled-components';
            const _styled2 = typeof _styled2styled_components.default === 'undefined' ? _styled2styled_components : _styled2styled_components.default;;
import _styledstyled_components from 'styled-components';
            const _styled = typeof _styledstyled_components.default === 'undefined' ? _styledstyled_components : _styledstyled_components.default;;
import { css as _css } from "styled-components";
import React, { forwardRef, useMemo, useRef, useState } from 'react';
import { defaultEasing, defaultSpeed, defaultOpacity, horizontalOffset, maxMoveOffset, defaultDragEasing } from './variables';
import isTouchDevice from './utils/isTouchDevice';
import { limitNumber } from './utils/limitTarget';
import useIsomorphicLayoutEffect from './hooks/useIsomorphicLayoutEffect';
import useAdjacentImages from './hooks/useAdjacentImages';
import useSetState from './hooks/useSetState';
import useEventListener from './hooks/useEventListener';
import useAnimationVisible from './hooks/useAnimationVisible';
import useMethods from './hooks/useMethods';
import SlidePortal from './components/SlidePortal';
import PreventScroll from './components/PreventScroll';
import PhotoBox from './PhotoBox';
import { keyframes } from 'styled-components';
const initialState = {
  loading: false,
  x: 0,
  y: 0,
  touched: false,
  pause: false,
  lastCX: undefined,
  lastCY: undefined,
  bg: undefined,
  lastBg: undefined,
  overlay: true,
  minimal: true,
  scale: 1,
  rotate: 0
};
const fade = keyframes(["from{opacity:0;}to{opacity:1;}"]);
const PhotoSlider = forwardRef((props, ref) => {
  const {
    mode: modeProp = 'slide',
    loop = 3,
    speed: speedFn,
    easing: easingFn,
    photoClosable,
    maskClosable = true,
    maskOpacity = defaultOpacity,
    pullClosable = true,
    overlayRender,
    className,
    maskClassName,
    photoClassName,
    photoWrapClassName,
    loadingElement,
    brokenElement,
    images,
    index: controlledIndex = 0,
    onIndexChange: controlledIndexChange,
    visible,
    onClose,
    afterClose,
    portalContainer,
    enableMouseZoom = true
  } = props;
  const isDragMode = useMemo(() => {
    if (isTouchDevice) return false;
    return modeProp === 'drag';
  }, [modeProp]);
  const [state, updateState] = useSetState(initialState);
  const [innerIndex, updateInnerIndex] = useState(0);
  const {
    loading,
    x,
    y,
    touched,
    pause,
    lastCX,
    lastCY,
    bg = maskOpacity,
    lastBg,
    overlay,
    minimal,
    scale,
    rotate,
    onScale,
    onRotate
  } = state;
  // 受控 index
  const isControlled = props.hasOwnProperty('index');
  const index = isControlled ? controlledIndex : innerIndex;
  const onIndexChange = isControlled ? controlledIndexChange : updateInnerIndex;
  // 内部虚拟 index
  const virtualIndexRef = useRef(index);

  // 当前图片
  const imageLength = images.length;
  const currentImage = images[index];

  // 是否开启
  // noinspection SuspiciousTypeOfGuard
  const enableLoop = typeof loop === 'boolean' ? loop : imageLength > loop;

  // 显示动画处理
  const [realVisible, activeAnimation, onAnimationEnd] = useAnimationVisible(visible, afterClose);
  useIsomorphicLayoutEffect(() => {
    // 显示弹出层，修正正确的指向
    if (realVisible) {
      updateState({
        pause: true,
        x: index * -(innerWidth + horizontalOffset)
      });
      virtualIndexRef.current = index;
      return;
    }
    // 关闭后清空状态
    updateState(initialState);
  }, [realVisible]);
  const {
    close,
    changeIndex
  } = useMethods({
    close(evt) {
      if (onRotate) {
        onRotate(0);
      }
      updateState({
        overlay: true,
        // 记录当前关闭时的透明度
        lastBg: bg
      });
      onClose(evt);
    },
    changeIndex(nextIndex, isPause = false) {
      // 当前索引
      const currentIndex = enableLoop ? virtualIndexRef.current + (nextIndex - index) : nextIndex;
      const max = imageLength - 1;
      // 虚拟 index
      // 非循环模式，限制区间
      const limitIndex = limitNumber(currentIndex, 0, max);
      const nextVirtualIndex = enableLoop ? currentIndex : limitIndex;
      // 单个屏幕宽度
      const singlePageWidth = innerWidth + horizontalOffset;
      updateState({
        touched: false,
        lastCX: undefined,
        lastCY: undefined,
        x: -singlePageWidth * nextVirtualIndex,
        pause: isPause
      });
      virtualIndexRef.current = nextVirtualIndex;
      // 更新真实的 index
      const realLoopIndex = nextIndex < 0 ? max : nextIndex > max ? 0 : nextIndex;
      if (onIndexChange) {
        onIndexChange(enableLoop ? realLoopIndex : limitIndex);
      }
    }
  });
  useEventListener('keydown', evt => {
    if (visible) {
      switch (evt.key) {
        case 'ArrowLeft':
          changeIndex(index - 1, true);
          break;
        case 'ArrowRight':
          changeIndex(index + 1, true);
          break;
        case 'Escape':
          close();
          break;
      }
    }
  });
  function handlePhotoTap(closeable) {
    return closeable ? close() : updateState({
      overlay: !overlay
    });
  }
  function handleResize() {
    updateState({
      x: -(innerWidth + horizontalOffset) * index,
      lastCX: undefined,
      lastCY: undefined,
      pause: isDragMode ? false : true
    });
    virtualIndexRef.current = index;
  }
  function handleReachVerticalMove(clientY, nextScale) {
    if (lastCY === undefined) {
      updateState({
        touched: true,
        lastCY: clientY,
        bg,
        minimal: true
      });
      return;
    }
    const opacity = maskOpacity === null ? null : limitNumber(maskOpacity, 0.01, maskOpacity - Math.abs(clientY - lastCY) / 100 / 4);
    updateState({
      touched: true,
      lastCY,
      bg: nextScale === 1 && !isDragMode ? opacity : maskOpacity,
      minimal: nextScale === 1
    });
  }
  function handleReachHorizontalMove(clientX) {
    if (lastCX === undefined) {
      updateState({
        touched: true,
        lastCX: clientX,
        x,
        pause: false
      });
      return;
    }
    const originOffsetClientX = clientX - lastCX;
    let offsetClientX = originOffsetClientX;

    // 第一张和最后一张超出距离减半
    if (!enableLoop && (index === 0 && originOffsetClientX > 0 || index === imageLength - 1 && originOffsetClientX < 0)) {
      offsetClientX = originOffsetClientX / 2;
    }
    updateState({
      touched: true,
      lastCX: lastCX,
      x: -(innerWidth + horizontalOffset) * virtualIndexRef.current + offsetClientX,
      pause: false
    });
  }
  function handleReachMove(reachPosition, clientX, clientY, nextScale) {
    if (isDragMode) {
      // handleReachDragMove
    } else if (reachPosition === 'x') {
      handleReachHorizontalMove(clientX);
    } else if (reachPosition === 'y') {
      handleReachVerticalMove(clientY, nextScale);
    }
  }
  function handleReachUp(clientX, clientY) {
    const offsetClientX = clientX - (lastCX ?? clientX);
    const offsetClientY = clientY - (lastCY ?? clientY);
    if (isDragMode) {
      updateState({
        touched: false
      });
      return;
    }
    let willClose = false;
    // 下一张
    if (offsetClientX < -maxMoveOffset) {
      changeIndex(index + 1);
      return;
    }
    // 上一张
    if (offsetClientX > maxMoveOffset) {
      changeIndex(index - 1);
      return;
    }
    const singlePageWidth = innerWidth + horizontalOffset;
    // 当前偏移
    const currentTranslateX = -singlePageWidth * virtualIndexRef.current;
    if (Math.abs(offsetClientY) > 100 && minimal && pullClosable) {
      willClose = true;
      close();
    }
    updateState({
      touched: false,
      x: currentTranslateX,
      lastCX: undefined,
      lastCY: undefined,
      bg: maskOpacity,
      overlay: willClose ? true : overlay
    });
  }
  // 截取相邻的图片
  const adjacentImages = useAdjacentImages(images, index, enableLoop);
  if (!realVisible) {
    return null;
  }
  const currentOverlayVisible = overlay && !activeAnimation;
  // 关闭过程中使用下拉保存的透明度
  const currentOpacity = visible ? bg : lastBg;
  // 覆盖物参数
  const overlayParams = onScale && onRotate && {
    loading,
    loop: enableLoop,
    mode: isDragMode ? 'drag' : 'slide',
    images,
    index,
    visible,
    onClose: close,
    onIndexChange: changeIndex,
    overlayVisible: currentOverlayVisible,
    overlay: currentImage && currentImage.overlay,
    scale,
    rotate,
    onScale,
    onRotate
  };
  // 动画时间
  const currentSpeed = speedFn ? speedFn(activeAnimation) : defaultSpeed;
  const currentEasing = easingFn ? easingFn(activeAnimation) : isDragMode ? defaultDragEasing : defaultEasing;
  const slideSpeed = speedFn ? speedFn(3) : defaultSpeed + 200;
  const slideEasing = easingFn ? easingFn(3) : defaultEasing;
  return <SlidePortal ref={ref} className={className} role="dialog" onClick={e => e.stopPropagation()} container={portalContainer}>
        {visible && <PreventScroll />}
        <_StyledDiv className={maskClassName} style={{
      background: currentOpacity ? `rgba(0, 0, 0, ${currentOpacity})` : undefined,
      transitionTimingFunction: currentEasing,
      transitionDuration: `${touched ? 0 : currentSpeed}ms`,
      animationDuration: `${currentSpeed}ms`
    }} onAnimationEnd={onAnimationEnd} $_css2={[{
      "position": "absolute",
      "left": "0px",
      "top": "0px",
      "zIndex": "-1",
      "height": "100%",
      "width": "100%",
      "--tw-bg-opacity": "1",
      "backgroundColor": "rgb(0 0 0 / var(--tw-bg-opacity))",
      "transitionProperty": "background-color",
      "transitionTimingFunction": "cubic-bezier(0.4, 0, 0.2, 1)",
      "transitionDuration": "150ms"
    }, (activeAnimation === 1 || activeAnimation === 2) && {
      "opacity": "0"
    }, activeAnimation === 1 && _css(["animation:", " linear both;"], fade), activeAnimation === 2 && _css(["animation:", " linear both reverse;"], fade)]} />
        {adjacentImages.map((item, currentIndex) => {
      // 截取之前的索引位置
      const nextIndex = !enableLoop && index === 0 ? index + currentIndex : virtualIndexRef.current - 1 + currentIndex;
      const isActive = !item.isCloned && (currentImage && currentImage.key) === item.key;
      return <PhotoBox key={enableLoop ? `${item.key}/${item.src}/${nextIndex}` : item.key} item={item} speed={currentSpeed} easing={currentEasing} visible={visible} onReachMove={handleReachMove} onReachUp={handleReachUp} onPhotoTap={() => handlePhotoTap(photoClosable)} onMaskTap={() => handlePhotoTap(maskClosable)} isDragMode={isDragMode} wrapClassName={photoWrapClassName} className={photoClassName} style={{
        opacity: isDragMode ? isActive ? 1 : 0 : undefined,
        left: `${(innerWidth + horizontalOffset) * nextIndex}px`,
        transform: `translate3d(${x}px, ${y}px, 0)`,
        transition: touched || pause ? undefined : `transform ${slideSpeed}ms ${slideEasing}`
      }} loadingElement={loadingElement} brokenElement={brokenElement} onPhotoResize={handleResize} isActive={isActive} expose={updateState} enableMouseZoom={enableMouseZoom} />;
    })}
        {overlayRender && overlayParams && <_StyledDiv2 $_css3={[!currentOverlayVisible && {
      "opacity": "0"
    }]}>{overlayRender(overlayParams)}</_StyledDiv2>}
      </SlidePortal>;
});
PhotoSlider.displayName = 'PhotoSlider';
export default PhotoSlider;
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-12i02f7-0"
})(["", ""], p => p.$_css2);
var _StyledDiv2 = _styled("div").withConfig({
  componentId: "sc-12i02f7-1"
})(["", ""], p => p.$_css3);